import {Box} from "@mui/material";
import {FC, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {NoteItemCard, IProps as NoteItemCardProps} from "./NoteItemCard";
import {useUpdateNote, useDeleteNote, NoteDto} from "@/app2/shared/data";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";

const useStyles = makeStyles()((theme) => ({
	cardRoot: {
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.palette.secondary.light}`
	},
	noteItem: {
		[theme.breakpoints.down("sm")]: {
			order: 1
		}
	},
	linkItem:{
		color: theme.palette.secondary.main,
		cursor: "pointer",
		textDecoration: "underline",
		...theme.transitionEffects.hoverFade.default,
		"&:hover": {
			...theme.transitionEffects.hoverFade.active
		}
	}
}));

export interface IPropsComponentOverrides {
	noteItemCard?: (props: NoteItemCardProps) => JSX.Element
}

interface ICustomProps {
	note: NoteDto,
	onGoToModuleAction?: (courseSlug: string, moduleSlug:string, moduleType:string, timestamp?:number) => void,
	className?:string,
	componentOverrides?: IPropsComponentOverrides
}

export type IProps = ICustomProps;

export const NoteItem:FC<IProps> = ({note, className, onGoToModuleAction, componentOverrides}) => {
	const {classes, cx} = useStyles();
	const [isEditing, setIsEditing] = useState(false);
	const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState<boolean>(false);
	const {t} = useSSTranslation();
	const {successSnackbar, errorSnackbar} = useCustomSnackbar();
	
	// React Query hooks for mutations
	const {mutate: updateNote, isPending: isUpdating} = useUpdateNote();
	const {mutate: deleteNote, isPending: isDeleting} = useDeleteNote();
	
	const {course, module} = note;
	const isLoading = isUpdating || isDeleting;

	const links:JSX.Element[] = []
	const isSourceInvalid = course?.slug === null || module?.slug === null

	if (isSourceInvalid) {
		links.push(<span key="invalid">COURSE/LESSON NO LONGER AVAILABLE</span>)
	}
	if (course && !module) {
		links.push(<span key="_deleted-lesson" style={{fontStyle: "italic"}}>{t("common_notes_deleted_lesson", "Deleted Lesson")}</span>)
	} else if (course && module && course?.slug && module?.slug) {
		links.push(
			<span 
				role="button" 
				tabIndex={0} 
				key="_lesson" 
				className={classes.linkItem} 
				onClick={()=> {
					onGoToModuleAction?.(
						course.slug ?? "", 
						module.slug ?? "", 
						module.type, 
						note.videoTimestamp ?? 0
					)
				}}
			>
				{module.title}
			</span>
		)
		links.push(<span key="_in"> {t("common_notes_in", "in")} </span>)
		links.push(
			<span 
				role="button" 
				tabIndex={0} 
				key="_course" 
				className={classes.linkItem} 
				onClick={()=> {
					onGoToModuleAction?.(
						course.slug ?? "", 
						module.slug ?? "", 
						module.type, 
						note.videoTimestamp ?? 0
					)
				}}
			>
				{course.title}
			</span>
		)
	}

	const handleToggleEditing = (): void => setIsEditing(state => !state);

	const handleUpdate = (newValue: string): void => {
		updateNote({
			noteId: note.id,
			content: newValue,
			videoTimestamp: note.videoTimestamp ?? null
		}, {
			onSuccess: () => {
				setIsEditing(false);
			}
		});
	}

	const handleDeleteNote = ():void => {
		deleteNote(note.id, {
			onSuccess: () => {
				setConfirmDeleteModalOpen(false);
				successSnackbar(t("notes_delete_success", "Note deleted"));
			},
			onError: () => {
				setConfirmDeleteModalOpen(false);
				errorSnackbar(t("notes_delete_error", "Failed to delete note. Please try again."));
			}
		});
	}

	const noteItemCardProps: NoteItemCardProps = {
		note,
		isSourceInvalid,
		onGoToModuleAction,
		isEditing,
		setIsEditing: setIsEditing,
		confirmDeleteModalOpen,
		setConfirmDeleteModalOpen,
		handleDeleteNote,
		handleUpdate,
		links,
		className: classes.cardRoot,
		isUpdating,
		isDeleting
	}

	return (
		<Box className={cx(classes.noteItem, className)}>
			{componentOverrides?.noteItemCard ? componentOverrides.noteItemCard(noteItemCardProps) : <NoteItemCard {...noteItemCardProps} />}
		</Box>
	)
}